import React, { useState, useEffect, useCallback } from 'react';
import api, { openDocument } from '../api';
import { format } from 'date-fns';
import { categoryLabel, SEND_CATEGORIES } from '../documentCategories';

const MAX_BYTES = 10 * 1024 * 1024; // matches the server's 10MB limit
const blankForm = { title: '', category: 'contract', requires_signature: true, file: null };
const fmt = (v) => { try { return format(new Date(v), 'dd/MM/yyyy'); } catch { return ''; } };

// Admin view of one person's documents, opened from Manage Staff: what the office
// has sent them (contracts, letters) with signature status, plus anything they
// uploaded themselves during onboarding. Works for staff and recruits alike.
const StaffDocuments = ({ person, onClose }) => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(blankForm);
  const [busy, setBusy] = useState(false);

  const firstName = person.name.split(' ')[0];

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/documents/recruit/${person.id}`);
      setDocs(r.data);
    } catch (err) {
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [person.id]);

  useEffect(() => { load(); }, [load]);

  const view = async (id) => {
    try { await openDocument(id); }
    catch (err) { setError('Could not open that document'); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (!form.title.trim()) return setError('Give the document a title');
    if (!form.file) return setError('Choose a file');
    if (form.file.size > MAX_BYTES) return setError('That file is over 10MB. Please use a smaller file.');
    setBusy(true);
    setError('');
    setSuccess('');
    const formEl = e.target;
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('title', form.title.trim());
      fd.append('category', form.category);
      fd.append('requires_signature', form.requires_signature);
      await api.post(`/api/documents/recruit/${person.id}/assign`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSuccess(`Document sent to ${person.name}`);
      setForm(blankForm);
      formEl.reset();
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (d) => {
    const warning = d.signature
      ? `Delete "${d.title}"? It has been signed, and the signature record will be deleted with it. This cannot be undone.`
      : `Delete "${d.title}"? This cannot be undone.`;
    if (!window.confirm(warning)) return;
    setError('');
    setSuccess('');
    try {
      await api.delete(`/api/documents/assigned/${d.id}`);
      setSuccess('Document deleted');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete');
    }
  };

  const sent = docs.filter(d => d.scope === 'assigned');
  const uploaded = docs.filter(d => d.scope === 'upload');

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-scroll" style={{ maxWidth: '680px' }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Documents: {person.name}</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          {error && <div className="alert alert-error" onClick={() => setError('')}>{error}</div>}
          {success && <div className="alert alert-success" onClick={() => setSuccess('')}>{success}</div>}

          <div style={sectionLabel}>Sent to {firstName}</div>
          {loading ? (
            <div className="loader">Loading...</div>
          ) : sent.length === 0 ? (
            <p style={muted}>Nothing sent yet.</p>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Title</th><th>Category</th><th>Added</th><th>Signature</th><th></th></tr></thead>
                <tbody>
                  {sent.map(d => (
                    <tr key={d.id}>
                      <td style={{ fontWeight: 500 }}>{d.title}</td>
                      <td>{categoryLabel(d.category)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmt(d.created_at)}</td>
                      <td>
                        {!d.requires_signature ? (
                          <span style={muted}>Not needed</span>
                        ) : d.signature ? (
                          <>
                            <span className="badge badge-approved">Signed</span>
                            <div style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '3px' }}>{d.signature.typed_name}, {fmt(d.signature.signed_at)}</div>
                          </>
                        ) : (
                          <span className="badge badge-pending">Awaiting signature</span>
                        )}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn btn-outline btn-sm" onClick={() => view(d.id)}>View</button>{' '}
                        <button className="btn btn-danger btn-sm" onClick={() => remove(d)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <form onSubmit={send} style={{ background: 'var(--grey-light)', border: '1px solid var(--border)', borderRadius: '6px', padding: '14px', margin: '16px 0' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--navy)', marginBottom: '4px' }}>Send {firstName} a document</div>
            <p style={{ ...muted, margin: '0 0 10px' }}>
              {person.role === 'recruit' ? 'They will see it on their onboarding page.' : 'They will see it under My Documents.'}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="sd-title">Title</label>
                <input id="sd-title" className="form-control" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Contract of Employment" maxLength={255} />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="sd-category">Category</label>
                <select id="sd-category" className="form-control" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                  {SEND_CATEGORIES.map(c => <option key={c} value={c}>{categoryLabel(c)}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" htmlFor="sd-file">File</label>
                <input id="sd-file" type="file" className="form-control" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" onChange={e => setForm(f => ({ ...f, file: e.target.files[0] || null }))} />
              </div>
              <div className="form-group" style={{ marginBottom: 0, display: 'flex', alignItems: 'flex-end' }}>
                <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', color: 'var(--navy)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.requires_signature} onChange={e => setForm(f => ({ ...f, requires_signature: e.target.checked }))} style={{ accentColor: 'var(--red)' }} />
                  Requires signature
                </label>
              </div>
            </div>
            <button className="btn btn-primary btn-sm" style={{ marginTop: '12px' }} disabled={busy}>{busy ? 'Sending...' : 'Send document'}</button>
          </form>

          <div style={sectionLabel}>Uploaded by {firstName}</div>
          {loading ? null : uploaded.length === 0 ? (
            <p style={muted}>Nothing uploaded.</p>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Title</th><th>Category</th><th>Added</th><th></th></tr></thead>
                <tbody>
                  {uploaded.map(d => (
                    <tr key={d.id}>
                      <td style={{ fontWeight: 500 }}>{d.title}</td>
                      <td>{categoryLabel(d.category)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{fmt(d.created_at)}</td>
                      <td style={{ textAlign: 'right' }}><button className="btn btn-outline btn-sm" onClick={() => view(d.id)}>View</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const sectionLabel = { fontSize: '11px', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--grey-mid)', fontWeight: 600, marginBottom: '8px' };
const muted = { fontSize: '13px', color: 'var(--grey-mid)' };

export default StaffDocuments;
